import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const useGroomingBookings = () => {
    const { authorizedFetch } = useAuth();
    
    const [bookings, setBookings] = useState([]);
    const [todayBookings, setTodayBookings] = useState([]);
    const [upcomingBookings, setUpcomingBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Fetch all bookings
    const fetchAllBookings = async () => {
        if (!authorizedFetch) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming?limit=10000`);

            if (!response.ok) {
                throw new Error(`Failed to fetch bookings: ${response.statusText}`);
            }
            
            const data = await response.json();
            setBookings(data.bookings || []);
        } catch (err) {
            console.error('Error fetching grooming bookings:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch today's bookings
    const fetchTodayBookings = async () => {
        if (!authorizedFetch) return;
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming/today`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch today's bookings: ${response.statusText}`);
            }
            
            const data = await response.json();
            setTodayBookings(data);
        } catch (err) {
            console.error('Error fetching today\'s grooming bookings:', err);
            setError(err.message);
        }
    };

    // Fetch upcoming bookings
    const fetchUpcomingBookings = async () => {
        if (!authorizedFetch) return;
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming/upcoming`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch upcoming bookings: ${response.statusText}`);
            }
            
            const data = await response.json();
            setUpcomingBookings(data);
        } catch (err) {
            console.error('Error fetching upcoming grooming bookings:', err);
            setError(err.message);
        }
    };

    // Create a new booking
    const createBooking = async (bookingData) => {
        if (!authorizedFetch) throw new Error('Not authenticated');
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(bookingData),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to create booking: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            // Refresh all data
            await Promise.all([
                fetchAllBookings(),
                fetchTodayBookings(),
                fetchUpcomingBookings()
            ]);
            
            return data.booking;
        } catch (err) {
            console.error('Error creating grooming booking:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Update a booking
    const updateBooking = async (bookingId, updates) => {
        if (!authorizedFetch) throw new Error('Not authenticated');
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming/${bookingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to update booking: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            // Refresh all data
            await Promise.all([
                fetchAllBookings(),
                fetchTodayBookings(),
                fetchUpcomingBookings()
            ]);
            
            return data.booking;
        } catch (err) {
            console.error('Error updating grooming booking:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Delete a booking
    const deleteBooking = async (bookingId) => {
        if (!authorizedFetch) throw new Error('Not authenticated');
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming/${bookingId}`, {
                method: 'DELETE',
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to delete booking: ${response.statusText}`);
            }
            
            // Refresh all data
            await Promise.all([
                fetchAllBookings(),
                fetchTodayBookings(),
                fetchUpcomingBookings()
            ]);
        } catch (err) {
            console.error('Error deleting grooming booking:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Get bookings for a specific dog
    const getBookingsByDog = async (dogId) => {
        if (!authorizedFetch) return [];
        
        try {
            const response = await authorizedFetch(`${API_URL}/api/grooming/dog/${dogId}`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch dog bookings: ${response.statusText}`);
            }
            
            return await response.json();
        } catch (err) {
            console.error('Error fetching dog grooming bookings:', err);
            setError(err.message);
            return [];
        }
    };

    // Load all data when hook is first used
    useEffect(() => {
        if (authorizedFetch) {
            Promise.all([
                fetchAllBookings(),
                fetchTodayBookings(),
                fetchUpcomingBookings()
            ]);
        }
    }, [authorizedFetch]);

    // Clear error after 5 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const clearError = () => setError('');

    return {
        bookings,
        todayBookings,
        upcomingBookings,
        loading,
        error,
        createBooking,
        updateBooking,
        deleteBooking,
        getBookingsByDog,
        fetchAllBookings,
        fetchTodayBookings,
        fetchUpcomingBookings,
        clearError
    };
};

export default useGroomingBookings;